import { lazy } from 'react';

import LegacyRouteResolver from '@/components/layout/LegacyRouteResolver';
import MainLayout from '@/components/layout/MainLayout';
import { AuthGuard } from '@/components/routing/AuthGuard';
import { HomeRedirect } from '@/components/routing/HomeRedirect';
import { RoleGuard } from '@/components/routing/RoleGuard';
import { ErrorFallback } from '@/components/ui/ErrorFallback';
import { NotFoundScreen } from '@/components/ui/NotFoundScreen';
import { UnauthorizedScreen } from '@/components/ui/UnauthorizedScreen';
import { ROLES } from '@/consts/roles';
import { ROUTES } from '@/consts/routes';
import { env } from '@/env/env';
import { AcceptInvitationScreen } from '@/features/auth/accept-invitation';
import { LoginScreen } from '@/features/auth/login';
import { PasswordResetScreen } from '@/features/auth/password-reset';
import { SsoCallbackScreen } from '@/features/auth/sso-callback';
import { createBrowserRouter } from 'react-router';

const DefineComponentLibraryLayout = lazy(
  () => import('@/modules/PMS/features/define-component-library/components/DefineComponentLibraryLayout')
);
const ComponentsView = lazy(() => import('@/modules/PMS/views/DefineComponentLibrary/ComponentsView'));
const SparesView = lazy(() => import('@/modules/PMS/views/DefineComponentLibrary/SparesView'));
const JobsView = lazy(() => import('@/modules/PMS/views/DefineComponentLibrary/JobsView'));
const VesselComponentLibraryLayout = lazy(
  () => import('@/modules/PMS/features/vessel-components/components/VesselComponentLibraryLayout')
);
const VesselComponentsView = lazy(() => import('@/modules/PMS/views/VesselComponentLibrary/VesselComponentsView'));
const VesselJobsView = lazy(() => import('@/modules/PMS/views/VesselComponentLibrary/VesselJobsView'));
const VesselAssignView = lazy(() => import('@/modules/PMS/views/VesselAssign/VesselAssignView'));
const MainLibrariesView = lazy(() => import('@/modules/PMS/views/MainLibraries/MainLibrariesView'));
const FleetJobsView = lazy(() => import('@/modules/PMS/views/FleetJobs/FleetJobsView'));
const FleetJobDetailView = lazy(() => import('@/modules/PMS/views/FleetJobs/FleetJobDetailView'));
const RunningHoursView = lazy(() => import('@/modules/PMS/views/RunningHours/RunningHoursView'));
const ApprovalsView = lazy(() => import('@/modules/PMS/views/Approvals/ApprovalsView'));
const AbstractComponentEditPage = lazy(
  () => import('@/modules/PMS/views/DefineComponentLibrary/AbstractComponentEditPage')
);
const SpareEditPage = lazy(() => import('@/modules/PMS/views/DefineComponentLibrary/SpareEditPage'));
const JobEditPage = lazy(() => import('@/modules/PMS/views/DefineComponentLibrary/JobEditPage'));
const VesselComponentEditPage = lazy(
  () => import('@/modules/PMS/views/VesselComponentLibrary/VesselComponentEditPage')
);
const VesselSpareEditPage = lazy(() => import('@/modules/PMS/views/VesselComponentLibrary/VesselSpareEditPage'));
const VesselJobEditPage = lazy(() => import('@/modules/PMS/views/VesselComponentLibrary/VesselJobEditPage'));
const OverviewLayout = lazy(() => import('@/modules/PUR/features/overview/components/OverviewLayout'));
const RequisitionsView = lazy(() => import('@/modules/PUR/views/Overview/RequisitionsView'));
const CreateRequisitionView = lazy(() => import('@/modules/PUR/views/Requisition/CreateRequisitionView'));
const RequisitionPreviewView = lazy(() => import('@/modules/PUR/views/Requisition/RequisitionPreviewView'));
const RfqView = lazy(() => import('@/modules/PUR/views/Overview/RfqView'));
const PurchaseOrdersView = lazy(() => import('@/modules/PUR/views/Overview/PurchaseOrdersView'));
const VendorsView = lazy(() => import('@/modules/PUR/views/Vendors/VendorsView'));
const CatalogueLayout = lazy(() => import('@/modules/PUR/features/catalogue/components/CatalogueLayout'));
const CatalogueSparesView = lazy(() => import('@/modules/PUR/views/Catalogue/SparesView'));
const CatalogueLubricantsView = lazy(() => import('@/modules/PUR/views/Catalogue/LubricantsView'));
const CatalogueStoresView = lazy(() => import('@/modules/PUR/views/Catalogue/StoresView'));
const CatalogueSafetyView = lazy(() => import('@/modules/PUR/views/Catalogue/SafetyView'));
const CatalogueElectricalView = lazy(() => import('@/modules/PUR/views/Catalogue/ElectricalView'));
const PaymentsLayout = lazy(() => import('@/modules/ACC/components/PaymentsLayout'));
const PaymentsView = lazy(() => import('@/modules/ACC/views/PaymentsView'));
const TcInView = lazy(() => import('@/modules/VOY/views/TCIN/TcInView'));
const PaymentOrdersView = lazy(() => import('@/modules/ACC/views/PaymentOrdersView'));
const PaymentsViewV2 = lazy(() => import('@/modules/ACC/views/PaymentsViewV2'));
const ApiDocsPage = lazy(() => import('@/features/api-docs/views/ApiDocsPage'));
const EstimatorPage = lazy(() => import('@/modules/VOY/views/EstimatorPage'));
const HybridCalcPage = lazy(() => import('@/modules/VOY/views/HybridCalcPage'));
const MarketIndexDashboard = lazy(() => import('@/modules/VOY/pages/MarketIndexDashboard'));
const ContractsPage = lazy(() => import('@/modules/VOY/views/ContractsPage'));
const TenantSettingsPage = lazy(() => import('@/features/tenant-settings/components/TenantSettingsPage'));
const MenuEditorPage = lazy(() => import('@/features/menu-editor/components/MenuEditorPage'));
const BunkerPriceLastView = lazy(() => import('@/modules/BUN/views/BunkerPriceLastView'));

const baseName = env.VITE_MOCK_MODE === 'true' ? '/' : env.VITE_BASE_URL;

export const router = createBrowserRouter(
  [
    { path: ROUTES.auth.login, element: <LoginScreen /> },
    { path: ROUTES.user.passwordReset, element: <PasswordResetScreen /> },
    { path: ROUTES.auth.sso, element: <SsoCallbackScreen /> },
    { path: ROUTES.auth.unauthorized, element: <UnauthorizedScreen /> },
    { path: ROUTES.auth.signup, element: <AcceptInvitationScreen /> },
    { path: ROUTES.auth.acceptInvitation, element: <AcceptInvitationScreen /> },
    {
      path: '/',
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      errorElement: <ErrorFallback />,
      children: [
        { index: true, element: <HomeRedirect /> },
        { path: ROUTES.chartering.estimator.replace('/', ''), element: <EstimatorPage /> },
        { path: ROUTES.chartering.estimatorNew.replace('/', ''), element: <EstimatorPage /> },
        { path: ROUTES.chartering.hybridCalc.replace('/', ''), element: <HybridCalcPage /> },
        { path: ROUTES.chartering.marketIndices.replace('/', ''), element: <MarketIndexDashboard /> },
        { path: ROUTES.chartering.contracts.replace('/', ''), element: <ContractsPage /> },
        // BUN Monitor routes - protected by BUN.MONITOR role
        {
          element: <RoleGuard requiredRoles={[ROLES.BUN_MONITOR]} />,
          children: [{ path: ROUTES.bun.bunkerPriceLast.replace('/', ''), element: <BunkerPriceLastView /> }]
        },
        // PMS routes + API docs - protected by PMS.ADMIN role
        {
          element: <RoleGuard requiredRoles={[ROLES.PMS_ADMIN]} />,
          children: [
            // API Documentation routes
            {
              path: 'docs/api',
              element: <ApiDocsPage />,
              loader: () => ({ crumb: 'API Documentation' })
            },
            {
              path: 'docs/api/:module',
              element: <ApiDocsPage />,
              loader: () => ({ crumb: 'API Documentation' })
            },
            {
              path: 'docs/api/:module/:entity',
              element: <ApiDocsPage />,
              loader: () => ({ crumb: 'API Documentation' })
            },
            {
              path: ROUTES.pms.defineComponentLibrary.replace('/', ''),
              element: <DefineComponentLibraryLayout />,
              loader: () => ({ crumb: 'Component Library' }),
              children: [
                { index: true, element: <ComponentsView />, loader: () => ({ crumb: 'Components' }) },
                { path: 'components', element: <ComponentsView />, loader: () => ({ crumb: 'Components' }) },
                { path: 'spares', element: <SparesView />, loader: () => ({ crumb: 'Spares' }) },
                { path: 'jobs', element: <JobsView />, loader: () => ({ crumb: 'Jobs' }) }
              ]
            },
            {
              path: 'pms/define-component-library/components/new',
              element: <AbstractComponentEditPage />,
              loader: () => ({ crumb: 'New Component' })
            },
            {
              path: 'pms/define-component-library/components/:id/edit',
              element: <AbstractComponentEditPage />,
              loader: () => ({ crumb: 'Edit Component' })
            },
            // Spare edit pages - rendered outside DefineComponentLibraryLayout to hide top tabs
            {
              path: 'pms/define-component-library/spares/new',
              element: <SpareEditPage />,
              loader: () => ({ crumb: 'New Spare' })
            },
            {
              path: 'pms/define-component-library/spares/:id/edit',
              element: <SpareEditPage />,
              loader: () => ({ crumb: 'Edit Spare' })
            },
            // Job edit pages - rendered outside DefineComponentLibraryLayout to hide top tabs
            {
              path: 'pms/define-component-library/jobs/new',
              element: <JobEditPage />,
              loader: () => ({ crumb: 'New Job' })
            },
            {
              path: 'pms/define-component-library/jobs/:id/edit',
              element: <JobEditPage />,
              loader: () => ({ crumb: 'Edit Job' })
            },
            {
              path: ROUTES.pms.vesselComponentLibrary.replace('/', ''),
              element: <VesselComponentLibraryLayout />,
              loader: () => ({ crumb: 'Vessel Library' }),
              children: [
                { index: true },
                {
                  path: ':vesselId',
                  children: [
                    { index: true, element: <VesselComponentsView />, loader: () => ({ crumb: 'Components' }) },
                    { path: 'jobs', element: <VesselJobsView />, loader: () => ({ crumb: 'Jobs' }) }
                  ]
                }
              ]
            },
            {
              path: 'pms/vessels/:vesselId/assign',
              element: <VesselAssignView />,
              loader: () => ({ crumb: 'Assign Components' })
            },
            // Vessel Component edit pages
            {
              path: 'pms/vessels/:vesselId/components/new',
              element: <VesselComponentEditPage />,
              loader: () => ({ crumb: 'New Component' })
            },
            {
              path: 'pms/vessels/:vesselId/components/:componentId/edit',
              element: <VesselComponentEditPage />,
              loader: () => ({ crumb: 'Edit Component' })
            },
            // Vessel Spare edit pages
            {
              path: 'pms/vessels/:vesselId/spares/new',
              element: <VesselSpareEditPage />,
              loader: () => ({ crumb: 'New Spare' })
            },
            {
              path: 'pms/vessels/:vesselId/spares/:spareId/edit',
              element: <VesselSpareEditPage />,
              loader: () => ({ crumb: 'Edit Spare' })
            },
            // Vessel Job edit pages
            {
              path: 'pms/vessels/:vesselId/jobs/new',
              element: <VesselJobEditPage />,
              loader: () => ({ crumb: 'New Job' })
            },
            {
              path: 'pms/vessels/:vesselId/jobs/:jobId/edit',
              element: <VesselJobEditPage />,
              loader: () => ({ crumb: 'Edit Job' })
            },
            {
              element: <RoleGuard requiredRoles={[ROLES.PMS_ADMIN_TECHNICAL]} />,
              children: [
                {
                  path: ROUTES.pms.mainLibraries.replace('/', ''),
                  element: <MainLibrariesView />,
                  loader: () => ({ crumb: 'Main Libraries' })
                }
              ]
            },
            {
              path: ROUTES.pms.fleetJobDetail.replace('/', ''),
              element: <FleetJobDetailView />,
              loader: () => ({ crumb: 'Fleet Job' })
            },
            {
              path: ROUTES.pms.fleetJobDetail.replace('/', ''),
              element: <FleetJobDetailView />,
              loader: () => ({ crumb: 'Fleet Job' })
            },
            {
              path: ROUTES.pms.fleetJobs.replace('/', ''),
              element: <FleetJobsView />,
              loader: () => ({ crumb: 'Fleet Jobs' })
            },
            {
              path: ROUTES.pms.runningHours.replace('/', ''),
              element: <RunningHoursView />,
              loader: () => ({ crumb: 'Running Hours' }),
              children: [{ index: true }, { path: ':vesselId' }]
            },
            {
              path: ROUTES.pms.approvals.replace('/', ''),
              element: <ApprovalsView />,
              loader: () => ({ crumb: 'Approvals' })
            }
          ]
        },
        // PUR routes - protected by PUR.ADMIN role
        {
          element: <RoleGuard requiredRoles={[ROLES.PUR_ADMIN]} />,
          children: [
            {
              path: ROUTES.pur.overview.replace('/', ''),
              element: <OverviewLayout />,
              loader: () => ({ crumb: 'Overview' }),
              children: [
                { index: true, element: <RequisitionsView />, loader: () => ({ crumb: 'Requisitions' }) },
                { path: 'requisitions', element: <RequisitionsView />, loader: () => ({ crumb: 'Requisitions' }) },
                { path: 'rfq', element: <RfqView />, loader: () => ({ crumb: 'RFQ' }) },
                {
                  path: 'purchase-orders',
                  element: <PurchaseOrdersView />,
                  loader: () => ({ crumb: 'Purchase Orders' })
                }
              ]
            },
            {
              path: ROUTES.pur.requisitionNew.replace('/', ''),
              element: <CreateRequisitionView />,
              loader: () => ({ crumb: 'New Requisition' })
            },
            {
              path: 'pur/requisition/:id',
              element: <RequisitionPreviewView />,
              loader: () => ({ crumb: 'Requisition Details' })
            },
            {
              path: ROUTES.pur.catalogue.replace('/', ''),
              element: <CatalogueLayout />,
              loader: () => ({ crumb: 'Catalogue' }),
              children: [
                { index: true, element: <CatalogueSparesView />, loader: () => ({ crumb: 'Spares' }) },
                { path: 'spares', element: <CatalogueSparesView />, loader: () => ({ crumb: 'Spares' }) },
                { path: 'lubricants', element: <CatalogueLubricantsView />, loader: () => ({ crumb: 'Lubricants' }) },
                { path: 'stores', element: <CatalogueStoresView />, loader: () => ({ crumb: 'Stores' }) },
                { path: 'safety', element: <CatalogueSafetyView />, loader: () => ({ crumb: 'Safety' }) },
                { path: 'electrical', element: <CatalogueElectricalView />, loader: () => ({ crumb: 'Electrical' }) }
              ]
            },
            {
              path: ROUTES.pur.vendors.replace('/', ''),
              element: <VendorsView />,
              loader: () => ({ crumb: 'Vendors' })
            }
          ]
        },
        {
          element: <RoleGuard requiredRoles={[ROLES.ACC_JUNIOR, ROLES.ACC_SENIOR, ROLES.ACC_VIEW_V1]} />,
          children: [
            {
              path: ROUTES.acc.payments.replace('/', ''),
              element: <PaymentsLayout />,
              loader: () => ({ crumb: 'Payments v1' }),
              children: [
                {
                  index: true,
                  element: <PaymentsView />,
                  loader: () => ({ crumb: 'Payment Instructions' })
                },
                {
                  path: 'orders',
                  element: <PaymentOrdersView />,
                  loader: () => ({ crumb: 'Payment Orders' })
                }
              ]
            }
          ]
        },
        {
          element: <RoleGuard requiredRoles={[ROLES.ACC_VIEW_V2]} />,
          children: [
            {
              path: ROUTES.acc.paymentsV2.replace('/', ''),
              element: <PaymentsViewV2 />,
              loader: () => ({ crumb: 'Payments' })
            }
          ]
        },

        // VOY routes
        {
          element: <RoleGuard requiredRoles={[ROLES.TCIN_VIEWER]} />,
          children: [
            {
              path: ROUTES.voy.tcin.replace('/', ''),
              element: <TcInView />,
              loader: () => ({ crumb: 'TC-IN' })
            }
          ]
        },

        // Admin routes - protected by ADM role
        {
          element: <RoleGuard requiredRoles={[ROLES.ADM]} />,
          children: [
            {
              path: ROUTES.admin.tenantSettings.replace('/', ''),
              element: <TenantSettingsPage />,
              loader: () => ({ crumb: 'Tenant Settings' })
            },
            {
              path: ROUTES.admin.menuEditor.replace('/', ''),
              element: <MenuEditorPage />,
              loader: () => ({ crumb: 'Menu Editor' })
            }
          ]
        },
        // Catch-all: resolve legacy routes from JSON navigation config
        { path: '*', element: <LegacyRouteResolver /> }
      ]
    },
    { path: '*', element: <NotFoundScreen /> }
  ],
  { basename: baseName }
);
